/**
* Reusable form field component with error display
*/

import { cn } from '@lib/utils/helpers'
import type { ReactNode } from 'react'

interface FormFieldProps {
  label: string
  required?: boolean
  children: ReactNode
  className?: string
  labelClassName?: string
}

/**
* FormField component that wraps an input/select with label
* Note: Error display is handled by FormInput/FormSelect components
*/
export function FormField({
  label,
  required = false,
  children,
  className,
  labelClassName,
}: FormFieldProps) {
  return (
    <div className={cn('min-w-0', className)}>
      <label className={cn('block text-sm font-medium text-gray-700 mb-2', labelClassName)}>
        {label}
        {required && '*'}
      </label>
      {children}
    </div>
  )
}

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string
}

/**
* FormInput component with consistent styling and error handling
*/
export function FormInput({
  error,
  className,
  ...props
}: FormInputProps) {
  return (
    <>
      <input
        {...props}
        className={cn(
          'w-full px-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder:text-gray-500',
          error ? '!border-red-500 !focus:ring-red-500 !focus:border-red-500' : 'border-gray-300',
          className
        )}
      />
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </>
  )
}

interface FormSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  error?: string
}

/**
* FormSelect component with consistent styling and error handling
*/
export function FormSelect({
  error,
  className,
  children,
  ...props
}: FormSelectProps) {
  return (
    <>
      <select
        {...props}
        className={cn(
          'w-full px-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-left placeholder:text-gray-500',
          error ? '!border-red-500 !focus:ring-red-500 !focus:border-red-500' : 'border-gray-300',
          className
        )}
      >
        {children}
      </select>
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </>
  )
}

 